import { useAPI } from '@providers/useAPI';
import { AuditLogsService, PaginatedAuditLogResponseDto, PrismaAuditLogDto } from 'app/API';
import { useClampListPage } from 'app/hooks/useClampListPage';
import { useDebouncedValue } from 'app/hooks/useDebouncedValue';
import { useListQueryParams } from 'app/hooks/useListQueryParams';
import { parseEndOfDayFromQueryParam } from '@helpers/parseEndOfDayFromQueryParam';
import { parseStartOfDayFromQueryParam } from '@helpers/parseStartOfDayFromQueryParam';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { auditActorTypes, TAuditActorType } from '../helpers/auditActorTypes';
import { auditBusinessActions, TAuditBusinessAction } from '../helpers/auditBusinessActions';
import { isUnusableAuditIdFilter, toAuditIdQuery } from '../helpers/isAuditUuid';
import { toAuditLogOrderBy } from '../helpers/auditLogOrderBy';
import { toUnixSecondsParam } from '../helpers/toUnixSecondsParam';

const TEXT_FILTER_DEBOUNCE_MS = 300;

const parseListParam = (key: string): string[] => {
  const parsed: unknown = JSON.parse(key);
  return Array.isArray(parsed)
    ? parsed.filter((value): value is string => typeof value === 'string' && value !== '')
    : [];
};

const readEnumList = <T extends string>(key: string, allowed: readonly T[]): T[] =>
  parseListParam(key).flatMap((value) => allowed.filter((item) => item === value));

export type TAuditLogFilters = {
  entityType: string[];
  entityId: string;
  actorType: TAuditActorType[];
  businessAction: TAuditBusinessAction[];
  actorUserId: string;
  actorLabel: string;
  createdFrom: string;
  createdTo: string;
};

export type TAuditLogListFilterName = 'entityType' | 'actorType' | 'businessAction';
export type TAuditLogTextFilterName = 'entityId' | 'actorUserId' | 'actorLabel';

const emptyFilters: TAuditLogFilters = {
  entityType: [],
  entityId: '',
  actorType: [],
  businessAction: [],
  actorUserId: '',
  actorLabel: '',
  createdFrom: '',
  createdTo: '',
};

type TState = {
  auditLogs: PaginatedAuditLogResponseDto;
  entityTypes: string[];
  isLoading: boolean;
  hasError: boolean;
  filters: TAuditLogFilters;
  setFilter: (name: TAuditLogTextFilterName, value: string) => void;
  setListFilter: (name: TAuditLogListFilterName, values: string[]) => void;
  setCreatedRange: (from: string, to: string) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
  selectedAuditLog: PrismaAuditLogDto | undefined;
  selectAuditLog: (auditLog: PrismaAuditLogDto | undefined) => void;
  sortingField: string;
  sortingReverse: boolean;
  toggleSort: (field: string) => void;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
};

const PageContext = createContext<TState>({
  auditLogs: { data: [], count: 0 },
  entityTypes: [],
  isLoading: false,
  hasError: false,
  filters: emptyFilters,
  setFilter: () => null,
  setListFilter: () => null,
  setCreatedRange: () => null,
  clearFilters: () => null,
  hasActiveFilters: false,
  selectedAuditLog: undefined,
  selectAuditLog: () => null,
  sortingField: 'createdAt',
  sortingReverse: true,
  toggleSort: () => null,
  page: 1,
  pageSize: 25,
  setPage: () => null,
  setPageSize: () => null,
});

type TProps = {
  children: ReactNode;
};

export const PageProvider = ({ children }: TProps) => {
  const { call } = useAPI();
  const { searchParams, page, pageSize, orderBy, orderType, setPage, setPageSize, setSort, updateParams } =
    useListQueryParams({
      defaults: { pageSize: 25, orderBy: 'createdAt', orderType: 'desc' },
    });
  const sortField = toAuditLogOrderBy(orderBy) ?? 'createdAt';
  const toggleSort = useCallback(
    (field: string) => setSort(field, sortField === field && orderType === 'asc' ? 'desc' : 'asc'),
    [setSort, sortField, orderType],
  );

  const [auditLogs, setAuditLogs] = useState<PaginatedAuditLogResponseDto>({ data: [], count: 0 });
  const [entityTypes, setEntityTypes] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [hasFreshData, setHasFreshData] = useState(false);
  const [selectedAuditLog, setSelectedAuditLog] = useState<PrismaAuditLogDto | undefined>();

  const entityTypeParam = JSON.stringify(searchParams.getAll('entityType'));
  const actorTypeParam = JSON.stringify(searchParams.getAll('actorType'));
  const businessActionParam = JSON.stringify(searchParams.getAll('businessAction'));
  const entityTypeList = useMemo(() => parseListParam(entityTypeParam), [entityTypeParam]);
  const actorTypeList = useMemo(() => readEnumList(actorTypeParam, auditActorTypes), [actorTypeParam]);
  const businessActionList = useMemo(
    () => readEnumList(businessActionParam, auditBusinessActions),
    [businessActionParam],
  );

  const filters = useMemo<TAuditLogFilters>(() => {
    return {
      entityType: entityTypeList,
      entityId: searchParams.get('entityId') ?? '',
      actorType: actorTypeList,
      businessAction: businessActionList,
      actorUserId: searchParams.get('actorUserId') ?? '',
      actorLabel: searchParams.get('actorLabel') ?? '',
      createdFrom: searchParams.get('createdFrom') ?? '',
      createdTo: searchParams.get('createdTo') ?? '',
    };
  }, [searchParams, entityTypeList, actorTypeList, businessActionList]);

  const debouncedEntityId = useDebouncedValue(filters.entityId, TEXT_FILTER_DEBOUNCE_MS);
  const debouncedActorUserId = useDebouncedValue(filters.actorUserId, TEXT_FILTER_DEBOUNCE_MS);
  const debouncedActorLabel = useDebouncedValue(filters.actorLabel.trim(), TEXT_FILTER_DEBOUNCE_MS);

  const setFilter = useCallback(
    (name: TAuditLogTextFilterName, value: string) => {
      updateParams({ [name]: value || null }, { resetPage: true });
    },
    [updateParams],
  );

  const setListFilter = useCallback(
    (name: TAuditLogListFilterName, values: string[]) => {
      updateParams({ [name]: values }, { resetPage: true });
    },
    [updateParams],
  );

  const setCreatedRange = useCallback(
    (from: string, to: string) => {
      updateParams({ createdFrom: from || null, createdTo: to || null }, { resetPage: true });
    },
    [updateParams],
  );

  const clearFilters = useCallback(() => {
    updateParams(
      {
        entityType: null,
        entityId: null,
        actorType: null,
        businessAction: null,
        actorUserId: null,
        actorLabel: null,
        createdFrom: null,
        createdTo: null,
      },
      { resetPage: true },
    );
  }, [updateParams]);

  const hasActiveFilters = useMemo(() => Object.values(filters).some((value) => value.length > 0), [filters]);

  useEffect(() => {
    let cancelled = false;

    const loadEntityTypes = async () => {
      try {
        const response = await call(() => AuditLogsService.adminListAuditLogEntityTypes());
        if (!cancelled) {
          setEntityTypes(response.entityTypes);
        }
      } catch {
        if (!cancelled) {
          setEntityTypes([]);
        }
      }
    };

    void loadEntityTypes();

    return () => {
      cancelled = true;
    };
  }, [call]);

  useEffect(() => {
    let cancelled = false;

    const loadAuditLogs = async () => {
      if (isUnusableAuditIdFilter(debouncedEntityId) || isUnusableAuditIdFilter(debouncedActorUserId)) {
        setAuditLogs({ data: [], count: 0 });
        setHasFreshData(false);
        setHasError(false);
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setHasError(false);
      try {
        const response = await call(() =>
          AuditLogsService.adminListAuditLogs({
            pageNo: String(page),
            pageSize: String(pageSize),
            orderBy: sortField,
            orderType,
            entityType: filters.entityType.length > 0 ? filters.entityType : undefined,
            entityId: toAuditIdQuery(debouncedEntityId),
            actorUserId: toAuditIdQuery(debouncedActorUserId),
            actorLabel: debouncedActorLabel || undefined,
            actorType: filters.actorType.length > 0 ? filters.actorType : undefined,
            businessAction: filters.businessAction.length > 0 ? filters.businessAction : undefined,
            createdFrom: toUnixSecondsParam(parseStartOfDayFromQueryParam(filters.createdFrom)),
            createdTo: toUnixSecondsParam(parseEndOfDayFromQueryParam(filters.createdTo)),
          }),
        );
        if (!cancelled) {
          setAuditLogs(response);
          setHasFreshData(true);
        }
      } catch {
        if (!cancelled) {
          setAuditLogs({ data: [], count: 0 });
          setHasFreshData(false);
          setHasError(true);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void loadAuditLogs();

    return () => {
      cancelled = true;
    };
  }, [
    call,
    page,
    pageSize,
    sortField,
    orderType,
    filters.entityType,
    filters.actorType,
    filters.businessAction,
    filters.createdFrom,
    filters.createdTo,
    debouncedEntityId,
    debouncedActorUserId,
    debouncedActorLabel,
  ]);

  useClampListPage({ page, setPage, pageSize, count: auditLogs.count, hasFreshData });

  // Filters, sorting and paging can drop the selected row from the results. Derive the drawer
  // from what is actually on screen so it cannot show an entry the current view excludes.
  const visibleSelectedAuditLog = useMemo(
    () => auditLogs.data.find((auditLog) => auditLog.id === selectedAuditLog?.id),
    [auditLogs, selectedAuditLog],
  );

  return (
    <PageContext.Provider
      value={{
        auditLogs,
        entityTypes,
        isLoading,
        hasError,
        filters,
        setFilter,
        setListFilter,
        setCreatedRange,
        clearFilters,
        hasActiveFilters,
        selectedAuditLog: visibleSelectedAuditLog,
        selectAuditLog: setSelectedAuditLog,
        sortingField: sortField,
        sortingReverse: orderType === 'desc',
        toggleSort,
        page,
        pageSize,
        setPage,
        setPageSize,
      }}
    >
      {children}
    </PageContext.Provider>
  );
};

export const usePageContext = () => useContext(PageContext);
